import { useMemo, useState } from "react";
import {
  Badge,
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from "@fluentui/react-components";
import {
  DEFAULT_CAPACITY,
  DEFAULT_PTO_DAYS,
  DEFAULT_TIME_ZONE,
  inEffect,
  PAY_AMOUNT_LABEL,
  PAY_TYPE_LABEL,
  PAY_TYPES,
  STATES,
  today,
  validateProfile,
  validateRecord,
  type PayType,
  type Person,
} from "../../../core";
import { errorText, send, useApi, type PeopleResponse, type ReferenceResponse } from "../api";
import { DateInput } from "../DateInput";
import { count, perHour } from "../format";
import { go, href } from "../router";
import { ErrorBar, Loading, Page, useMe, useShowArchived } from "../ui";


export function PeopleList() {
  const me = useMe();
  const people = useApi<PeopleResponse>("/people");
  const ref = useApi<ReferenceResponse>("/reference");
  // "Show archived" is remembered in this browser (rev. 1.6), so it stays as you left it.
  const [showArchived, setShowArchived] = useShowArchived("people");
  const [adding, setAdding] = useState(false);
  const roleName = useMemo(() => new Map(ref.data?.roles.roles.map((r) => [r.id, r.name])), [ref.data]);
  const day = today();
  const rows = (people.data?.people ?? []).filter((p) => showArchived || p.status !== "archived");
  const archived = (people.data?.people ?? []).filter((p) => p.status === "archived").length;

  return (
    <Page
      title="People"
      actions={
        <>
          {archived > 0 && <Switch label={`Show archived (${archived})`} checked={showArchived} onChange={(_, d) => setShowArchived(d.checked)} />}
          {me.can("people.edit") && (
            <Button appearance="primary" onClick={() => setAdding(true)}>
              Add person
            </Button>
          )}
        </>
      }
    >
      <ErrorBar message={people.error ?? ref.error} />
      {people.loading && !people.data ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Body1>No one is on the list yet. Add everyone who carries cost: employees and 1099 providers.</Body1>
      ) : (
        <Table size="small" className="data-table" aria-label="People">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Pay type</TableHeaderCell>
              <TableHeaderCell>Role</TableHeaderCell>
              <TableHeaderCell>State</TableHeaderCell>
              <TableHeaderCell className="num">Capacity</TableHeaderCell>
              <TableHeaderCell className="num">Base rate</TableHeaderCell>
              <TableHeaderCell className="num">OTE rate</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => {
              const card = p.rateCard;
              const capacity = inEffect(p.capacity, day);
              return (
                <TableRow key={p.id} className="clickable" onClick={() => go("people", p.id)}>
                  <TableCell>
                    <a href={href("people", p.id)} onClick={(e) => e.stopPropagation()}>
                      {p.name}
                    </a>{" "}
                    {p.status === "archived" && (
                      <Badge appearance="outline" size="small">
                        Archived
                      </Badge>
                    )}
                    {card && card.warnings.length > 0 && (
                      <Tooltip content={card.warnings.join(" ")} relationship="description">
                        <Badge appearance="tint" color="warning" size="small" className="warn-badge">
                          {card.warnings.length === 1 ? "1 note" : `${card.warnings.length} notes`}
                        </Badge>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{card?.type ? PAY_TYPE_LABEL[card.type] : "—"}</TableCell>
                  <TableCell>{(p.roleId && roleName.get(p.roleId)) ?? "—"}</TableCell>
                  <TableCell>{inEffect(p.locations, day)?.state ?? "—"}</TableCell>
                  <TableCell className="num">{capacity ? `${count(capacity.hoursPerWeek)} h/wk` : "—"}</TableCell>
                  <TableCell className="num">{card?.type ? perHour(card.baseRate) : "—"}</TableCell>
                  <TableCell className="num">{card?.type ? perHour(card.oteRate) : "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {adding && <NewPersonDialog roles={ref.data?.roles.roles ?? []} onClose={() => setAdding(false)} />}
    </Page>
  );
}

function NewPersonDialog({ roles, onClose }: { roles: { id: string; name: string; active: boolean }[]; onClose: () => void }) {
  const [form, setForm] = useState({
    name: "",
    title: "",
    roleId: "",
    email: "",
    type: "exempt" as PayType,
    amount: "",
    start: today(),
    state: "",
    hoursPerWeek: String(DEFAULT_CAPACITY),
    ptoDays: String(DEFAULT_PTO_DAYS),
  });
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof form) => (_: unknown, d: { value: string }) => setForm((f) => ({ ...f, [k]: d.value }));

  const payload = {
    profile: { name: form.name, title: form.title, roleId: form.roleId, email: form.email, timeZone: DEFAULT_TIME_ZONE },
    start: form.start,
    comp: { type: form.type, amount: Number(form.amount.replace(/[$,\s]/g, "")) },
    capacity: { hoursPerWeek: Number(form.hoursPerWeek), ptoDays: Number(form.ptoDays) },
    state: form.state || undefined,
  };
  const problems = [
    ...validateProfile({ ...payload.profile, payIds: [], status: "active" }),
    ...validateRecord("comp", { ...payload.comp, from: form.start }),
    ...validateRecord("capacity", { ...payload.capacity, from: form.start }),
  ];

  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      const { person } = await send<{ person: Person }>("POST", "/people", payload);
      go("people", person.id);
    } catch (err) {
      setError(errorText(err));
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className="dialog-wide">
        <DialogBody>
          <DialogTitle>Add a person</DialogTitle>
          <DialogContent className="form-grid">
            <Field label="Name" required>
              <Input value={form.name} onChange={set("name")} autoFocus />
            </Field>
            <Field label="Title">
              <Input value={form.title} onChange={set("title")} />
            </Field>
            <Field label="Role" hint="Placeholders in plans cost at the role's standard rate.">
              <Select value={form.roleId} onChange={set("roleId")}>
                <option value="">None yet</option>
                {roles.filter((r) => r.active).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="3Seven account" hint="Optional. Links their calendar hours later.">
              <Input type="email" value={form.email} onChange={set("email")} placeholder="name@3sevenconsulting.com" />
            </Field>
            <Field label="Pay type" required>
              <Select value={form.type} onChange={set("type")}>
                {PAY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {PAY_TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={PAY_AMOUNT_LABEL[form.type]} required>
              <Input value={form.amount} onChange={set("amount")} contentBefore="$" inputMode="decimal" />
            </Field>
            <Field label="Start date" required hint="Pay, capacity and state all take effect from this day.">
              <DateInput type="date" value={form.start} onChange={set("start")} />
            </Field>
            <Field label="Home work state" hint="Sets state unemployment tax.">
              <Select value={form.state} onChange={set("state")}>
                <option value="">Not set</option>
                {STATES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </Select>
            </Field>
            <Field label="Capacity (hours a week)" required>
              <Input type="number" value={form.hoursPerWeek} onChange={set("hoursPerWeek")} min={1} max={80} />
            </Field>
            <Field label="Planned PTO (days a year)" required>
              <Input type="number" value={form.ptoDays} onChange={set("ptoDays")} min={0} max={60} />
            </Field>
          </DialogContent>
          {form.name && form.amount && problems.length > 0 && <ErrorBar message={problems.join(" ")} />}
          <ErrorBar message={error} />
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={saving || problems.length > 0} onClick={() => void save()}>
              {saving ? "Adding…" : "Add person"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
